import { useState, useMemo } from 'react';
import type { DrilldownEvent } from '../../utils/drilldown';
import styles from './DataTable.module.css';

interface DataTableProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  pageSize?: number;
  dimensions?: string[];
  onDrillDown?: (event: DrilldownEvent) => void;
}

export default function DataTable({
  data,
  pageSize = 10,
  dimensions,
  onDrillDown,
}: DataTableProps) {
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);

  const sortedRows = useMemo(() => {
    if (!sortCol) return data.rows;
    return [...data.rows].sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data.rows, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const visibleRows = sortedRows.slice(page * pageSize, (page + 1) * pageSize);

  function handleSort(col: string) {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(0);
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {data.columns.map((col) => (
                <th key={col} onClick={() => handleSort(col)} className={styles.th}>
                  {col}
                  {sortCol === col && (
                    <span className={styles.sortIndicator}>{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => (
              <tr
                key={i}
                className={`${i % 2 === 0 ? styles.evenRow : ''} ${onDrillDown ? styles.clickableRow : ''}`}
                onClick={() => {
                  if (!onDrillDown || !dimensions?.length) return;
                  const dimField = dimensions[0];
                  const dimValue = String(row[dimField] ?? '');
                  if (dimValue) {
                    onDrillDown({ dimensionValue: dimValue, dimensionField: dimField });
                  }
                }}
              >
                {data.columns.map((col) => (
                  <td key={col} className={styles.td}>
                    {row[col] != null ? String(row[col]) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          <span className={styles.pageInfo}>
            Page {page + 1} of {totalPages} ({sortedRows.length} rows)
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
